import { Component } from '@theme/component';
import {
  debounce,
  formatLiteralSearchQuery,
  matchesReferencePatternSearch,
  normalizeReferencePatternSearch,
  onAnimationEnd,
  prefersReducedMotion,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const AVAILABILITY_FILTER = 'filter.v.availability';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [suggestionsListWrapper] - The desktop suggestions list container.
 * @property {HTMLElement} [sidebarSuggestionsPanel] - The desktop suggestions panel container.
 * @property {HTMLElement} [sidebarSuggestionsTitle] - The desktop suggestions panel title.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #patternSearchTimeout = null;

  #patternNormalizationTimeout = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#syncSidebarSuggestions();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.refs.searchInput.form?.addEventListener('submit', this.#handleSubmit, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #defaultSuggestionsTemplate() {
    return this.querySelector('[data-default-suggestions-template]');
  }

  get #searchUrl() {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    const searchTerm = this.refs.searchInput.value.trim();

    if (searchTerm) {
      searchUrl.searchParams.set('q', this.#formatSearchQuery(searchTerm));
    }

    return this.#applySearchDefaults(searchUrl);
  }

  #handleSubmit = (event) => {
    event.preventDefault();

    const form = event.currentTarget;

    if (!(form instanceof HTMLFormElement)) return;

    const searchUrl = new URL(form.action, location.origin);

    for (const [name, value] of new FormData(form)) {
      searchUrl.searchParams.append(name, value);
    }

    searchUrl.searchParams.set('q', this.#formatSearchQuery(this.refs.searchInput.value.trim()));
    window.location.href = this.#applySearchDefaults(searchUrl).toString();
  };

  get #shouldApplyAvailabilityDefault() {
    return this.dataset.applyAvailabilityDefault === 'true';
  }

  #formatSearchQuery(searchTerm) {
    return this.dataset.formatLiteralQuery === 'true' ? formatLiteralSearchQuery(searchTerm) : searchTerm;
  }

  /**
   * @param {URL} searchUrl
   * @returns {URL}
   */
  #applySearchDefaults(searchUrl) {
    if (this.#shouldApplyAvailabilityDefault && !searchUrl.searchParams.has(AVAILABILITY_FILTER)) {
      searchUrl.searchParams.set(AVAILABILITY_FILTER, '1');
    }

    return searchUrl;
  }

  get #viewAllLabel() {
    return this.dataset.viewAllLabel || 'Ver todo';
  }

  #toggleSidebarSuggestionsPanel() {
    const { sidebarSuggestionsPanel, sidebarSuggestionsTitle } = this.refs;

    if (!(sidebarSuggestionsPanel instanceof HTMLElement)) return;

    const hasSearchTerm = this.refs.searchInput.value.trim().length > 0;
    const hasNoResultsState = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__no-results') !== null;
    const shouldHide =
      (this.#emptyStateMode === 'top_search_terms' && !hasSearchTerm) || (hasSearchTerm && hasNoResultsState);

    sidebarSuggestionsPanel.hidden = shouldHide;

    if (sidebarSuggestionsTitle instanceof HTMLElement && shouldHide) {
      sidebarSuggestionsTitle.hidden = true;
    }
  }

  #restoreDefaultSuggestions() {
    const { suggestionsListWrapper, sidebarSuggestionsTitle } = this.refs;
    const template = this.#defaultSuggestionsTemplate;

    if (!(suggestionsListWrapper instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    suggestionsListWrapper.innerHTML = template.innerHTML.trim();
    suggestionsListWrapper.querySelectorAll('a[data-view-all-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = this.#searchUrl.toString();
      }
    });

    if (sidebarSuggestionsTitle instanceof HTMLElement) {
      sidebarSuggestionsTitle.hidden = true;
    }
  }

  get #emptyStateMode() {
    return this.dataset.emptyStateMode || 'carousel';
  }

  #syncSidebarSuggestions() {
    const { suggestionsListWrapper, predictiveSearchResults } = this.refs;

    if (!(suggestionsListWrapper instanceof HTMLElement)) return;

    const queryLinks = Array.from(
      predictiveSearchResults.querySelectorAll('.predictive-search-results__query-link')
    ).slice(0, 7);

    if (!queryLinks.length) {
      this.#restoreDefaultSuggestions();
      this.#toggleSidebarSuggestionsPanel();
      return;
    }

    const list = document.createElement('ul');
    list.className = 'tw:mt-3 tw:xl:mt-5 list-unstyled';
    list.setAttribute('role', 'list');

    queryLinks.forEach((queryLink) => {
      if (!(queryLink instanceof HTMLAnchorElement)) return;

      const item = document.createElement('li');
      const clonedLink = /** @type {HTMLAnchorElement} */ (queryLink.cloneNode(true));
      clonedLink.className =
        'tw:inline-flex tw:max-w-full tw:gap-3 tw:mt-3 tw:text-base tw:xl:text-xl tw:whitespace-normal tw:group tw:hover:text-garvaln-red';

      item.append(clonedLink);
      list.append(item);
    });

    const viewAllItem = document.createElement('li');
    const viewAllLink = document.createElement('a');
    viewAllLink.href = this.#searchUrl.toString();
    viewAllLink.className =
      'tw:inline-flex tw:max-w-full tw:gap-3 tw:mt-3 tw:text-base tw:xl:text-xl tw:whitespace-normal tw:lowercase tw:underline tw:decoration-inherit tw:group tw:hover:text-garvaln-red';
    viewAllLink.textContent = this.#viewAllLabel;
    viewAllItem.append(viewAllLink);
    list.append(viewAllItem);

    suggestionsListWrapper.replaceChildren(list);

    const { sidebarSuggestionsTitle } = this.refs;
    if (sidebarSuggestionsTitle instanceof HTMLElement) {
      sidebarSuggestionsTitle.hidden = false;
    }
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.#formatSearchQuery(this.refs.searchInput.value));
          window.location.href = this.#applySearchDefaults(searchUrl).toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const rawSearchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!rawSearchTerm.length) {
      this.#clearPatternNormalizationTimeout();
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    if (event.inputType === 'insertFromPaste') {
      const normalizedSearchTerm = this.#applyPatternNormalization(rawSearchTerm);

      if (normalizedSearchTerm !== rawSearchTerm) {
        return;
      }
    } else {
      this.#schedulePatternNormalization(rawSearchTerm);
    }
    this.#clearPatternSearchTimeout();
    this.#getSearchResults(rawSearchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '7');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,tag,variants.title,vendor,variants.sku,variants.barcode'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#syncSidebarSuggestions();
        this.#toggleSidebarSuggestionsPanel();
        this.#resetScrollPositions();

        const hasNoResults = this.#hasNoPredictiveResults();

        if (hasNoResults && matchesReferencePatternSearch(searchTerm)) {
          this.#showPatternSearchLoadingState();
        }

        if (hasNoResults) {
          this.#schedulePatternSearchFallback(searchTerm, true);
        } else {
          this.#clearPatternSearchTimeout();
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #clearPatternSearchTimeout() {
    if (this.#patternSearchTimeout) {
      window.clearTimeout(this.#patternSearchTimeout);
      this.#patternSearchTimeout = null;
    }
  }

  #clearPatternNormalizationTimeout() {
    if (this.#patternNormalizationTimeout) {
      window.clearTimeout(this.#patternNormalizationTimeout);
      this.#patternNormalizationTimeout = null;
    }
  }

  /**
   * @param {string} rawSearchTerm
   * @returns {string}
   */
  #applyPatternNormalization(rawSearchTerm) {
    const currentSearchTerm = this.refs.searchInput.value.trim();

    if (currentSearchTerm !== rawSearchTerm) {
      return currentSearchTerm;
    }

    const normalizedSearchTerm = normalizeReferencePatternSearch(rawSearchTerm);

    if (normalizedSearchTerm === rawSearchTerm) {
      return rawSearchTerm;
    }

    this.refs.searchInput.value = normalizedSearchTerm;
    this.#clearPatternSearchTimeout();
    this.#getSearchResults(normalizedSearchTerm);
    return normalizedSearchTerm;
  }

  /**
   * @param {string} rawSearchTerm
   */
  #schedulePatternNormalization(rawSearchTerm) {
    this.#clearPatternNormalizationTimeout();

    this.#patternNormalizationTimeout = window.setTimeout(() => {
      this.#applyPatternNormalization(rawSearchTerm);
    }, 2000);
  }

  #hasNoPredictiveResults() {
    return Boolean(this.refs.predictiveSearchResults.querySelector('.predictive-search-results__no-results'));
  }

  #showPatternSearchLoadingState() {
    const noResultsElement = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__no-results');

    if (!(noResultsElement instanceof HTMLElement)) {
      return;
    }

    noResultsElement.dataset.patternSearchLoading = 'true';
    noResultsElement.setAttribute('aria-live', 'polite');
    noResultsElement.innerHTML = '<p>Buscando...</p>';
  }

  /**
   * @param {string} searchTerm
   */
  #goToFullSearch(searchTerm) {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', this.#formatSearchQuery(searchTerm));
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[unavailable_products]', 'show');
    searchUrl.searchParams.set('options[prefix]', 'none');
    window.location.href = this.#applySearchDefaults(searchUrl).toString();
  }

  /**
   * @param {string} searchTerm
   * @param {boolean} waitForNoResults
   */
  #schedulePatternSearchFallback(searchTerm, waitForNoResults = false) {
    this.#clearPatternSearchTimeout();

    if (!matchesReferencePatternSearch(searchTerm)) {
      return;
    }

    this.#patternSearchTimeout = window.setTimeout(() => {
      const currentSearchTerm = this.refs.searchInput.value.trim();

      if (currentSearchTerm !== searchTerm) {
        return;
      }

      if (!waitForNoResults || this.#hasNoPredictiveResults()) {
        this.#goToFullSearch(searchTerm);
      }
    }, 2000);
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#clearPatternNormalizationTimeout();
    this.#clearPatternSearchTimeout();
    searchInput.value = '';
    this.#hideResetButton();
    this.#toggleSidebarSuggestionsPanel();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (this.#emptyStateMode === 'recently_viewed') {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        const recentlyViewedPlaceholder = parsedEmptySectionMarkup.querySelector('[data-recently-viewed-empty-state]');

        if (recentlyViewedProductsHtml && recentlyViewedPlaceholder) {
          recentlyViewedPlaceholder.replaceWith(recentlyViewedProductsHtml);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#syncSidebarSuggestions();
    this.#toggleSidebarSuggestionsPanel();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
